/** Most of the complicated stuff in this file is to work around known bugs and limitations of 3rd-party libraries.
    It would be great to delete a lot of code here AFTER the 3rd-party libs are better able to handle things. */

import { cloneDeep, compact} from 'lodash';
import { getFeature ,
  coordinatesToXPolygon,
  getCoordinates,
  getGeometry,
  multiPolygonToPolygonArray,
  polygonArrayToXPolygon
} from 'geometry/geojson/convert';
import { doesPolygonHaveArea } from 'geometry/geojson/area';

// 3rd-party wrapped
import { DEFAULT_CLIPPING_LIBRARY, clippingLibrary } from './constants';
import { union as turfUnion } from '3rd-party-wrappers/turf';
import { union as martinezUnion } from '3rd-party-wrappers/martinez-polygon-clipping';
import { union as clipperLibUnion } from '3rd-party-wrappers/clipper-lib';
import { union as cglUnion } from '@tcc/cgl';

// util
import { isValidXPolygon } from 'geometry/geojson/validator';
import {
  fixOobInnerRingsInXPolygon,
  fixPolygonsThatShouldBeInnerRingsInMultiPolygon
} from 'geometry/geojson/fix-holes';
import { logApiError, DISABLE_VALIDATION } from '3rd-party-wrappers/guarded-call';
import { workerLogError } from 'scripts/editZones.worker';
import { roundGeometry } from 'util/round';

const _unionClipperLib = (xPolygon1, xPolygon2) => clipperLibUnion(xPolygon1, xPolygon2);

const _unionTurf = (xPolygon1, xPolygon2) => {
  const geoJson = turfUnion(getFeature(xPolygon1), getFeature(xPolygon2));
  if (geoJson === undefined) { return undefined; }
  const xPolygon = getGeometry(geoJson);
  if (xPolygon.type === 'Polygon' || xPolygon.type === 'MultiPolygon') { return xPolygon; }

  logApiError({args:[xPolygon1, xPolygon2], funcName:'union', library:clippingLibrary.TURF,
    errorMessage:'unexpected geoJsonType returned from API'});
  return undefined;
};

const _unionMartinez = (xPolygon1, xPolygon2) => {
  const coordinates1 = roundGeometry(getCoordinates(xPolygon1));
  const coordinates2 = roundGeometry(getCoordinates(xPolygon2));
  const mergedCoordinates = martinezUnion(coordinates1, coordinates2, DISABLE_VALIDATION); // TODO--remove disable validation after the hole-fixing solution is not needed.
  if (mergedCoordinates === undefined) { return undefined; }
  let xPolygon = coordinatesToXPolygon(mergedCoordinates);

  // TODO--remove block below when hole-fixing solution not needed. Explained in geoJsonFixHoles.js
  fixOobInnerRingsInXPolygon(xPolygon);
  if (xPolygon.type === 'MultiPolygon') {
    xPolygon = fixPolygonsThatShouldBeInnerRingsInMultiPolygon(xPolygon);
  }
  const violations = [];
  if (!isValidXPolygon(xPolygon, violations)) {
    logApiError({args:[xPolygon1, xPolygon2], funcName:'union', library:clippingLibrary.MARTINEZ,
      errorMessage:'invalid xPolygon - ' + violations.join()});
    return undefined;
  }

  return xPolygon;
};

const _union = (xPolygon1, xPolygon2, useLibraries) => {
  if (!isValidXPolygon(xPolygon1) || !isValidXPolygon(xPolygon2)) {
    workerLogError({message:'invalid xPolygon params', context:{xPolygon1, xPolygon2}});
  }

  // Aware of the absurdity of this, and hope to one day delete it down to one call to a trusted lib.
  let xPolygon;
  useLibraries.forEach(library => {
    if (xPolygon === undefined) {
      if (library === 'MFOGEL') {
        const coordinates = cglUnion(workerLogError, xPolygon1.coordinates, xPolygon2.coordinates);
        if (coordinates.length === 0) {
          xPolygon = null;
        } else if(coordinates.length === 1) {
          xPolygon = {type: 'Polygon', coordinates: coordinates[0] };
        } else {
          xPolygon = { type: 'MultiPolygon', coordinates };
        }
      }
      if (library === clippingLibrary.TURF) {
        xPolygon = _unionTurf(xPolygon1, xPolygon2);
      } else if (library === clippingLibrary.MARTINEZ) {
        xPolygon = _unionMartinez(xPolygon1, xPolygon2);
      } else if (library === clippingLibrary.CLIPPERLIB) {
        xPolygon = _unionClipperLib(xPolygon1, xPolygon2);
      }
    }
  });

  return xPolygon;
};

/** Merge together (union) any Polygons in an array that intersect. A shared non-overlapping point or border should
    cause a merge.
    @param polygonArray Array of valid Polygons--no MultiPolygons. Empty array is okay.
    @param useLibraries An array with each element indicating library to use for clipping operations. Will be tried in
       in order.
    @return Array of merged Polygons that cover same area as passed-in Polygons. */
export const mergeIntersectingPolygonsInArray = (polygonArray, useLibraries = DEFAULT_CLIPPING_LIBRARY) => {
  // Remove any polygons without area from consideration.
  const mergedPolygons = cloneDeep(polygonArray)
  .map(polygon => doesPolygonHaveArea(polygon) ? polygon : null);

  mergedPolygons.forEach((leftPolygon, leftIndex) => {
    if (!leftPolygon) { return; }
    mergedPolygons.some((rightPolygon, rightIndex) => {
      if (rightIndex <= leftIndex || !rightPolygon) { return false; }
      const mergedGeometry = _union(leftPolygon, rightPolygon, useLibraries);
      if (mergedGeometry === undefined) { throw new Error('_union() failed.'); } // Errors already logged.
      if (mergedGeometry.type !== 'Polygon') { return false; } // MultiPolygon is returned for a non-intersecting union. Other types are also unusable.
      mergedPolygons[rightIndex] = mergedGeometry;
      mergedPolygons[leftIndex] = null;
      return true;
    });
  });
  const compacted = compact(mergedPolygons);
  return compacted;
};

/** Calculate the union shape between two or more geoJsons.
    @param geoJsons Two or more arguments, each expected be a Polygon or MultiPolygon
    @param useLibraries An array with each element indicating library to use for clipping operations. Will be tried in
       in order.
    @returns Polygon or MultiPolygon containing union of geoJson1/2. Or null, if no union. */
export const calculateUnion = ({geoJsons, useLibraries = DEFAULT_CLIPPING_LIBRARY}) => {
  if (geoJsons.length < 2) { throw new Error('bad param'); }
  let polygons = [];
  geoJsons.forEach(geoJson => {
    const geometry = getGeometry(geoJson);
    const geoJsonPolygons = geometry.type === 'Polygon' ? [geometry] : multiPolygonToPolygonArray(geometry);
    polygons = polygons.concat(geoJsonPolygons);
  });
  const mergedPolygons = mergeIntersectingPolygonsInArray(polygons, useLibraries);
  return mergedPolygons.length > 0 ? polygonArrayToXPolygon(mergedPolygons) : null;
};

export const unitTestPrivate = {
  _union
};
